import { FC } from 'react'
import type { Invoice, BusinessInfo, ClientInfo } from '@/types/invoice'
import { formatDate } from '@/utils/format'

interface InvoiceHeaderProps {
  business: BusinessInfo
  invoice: Invoice
  client: ClientInfo
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' },
  sent: { label: 'Awaiting Payment', color: '#b45309', bg: '#fef3c7' },
  viewed: { label: 'Awaiting Payment', color: '#b45309', bg: '#fef3c7' },
  paid: { label: 'Paid', color: '#166534', bg: '#dcfce7' },
  overdue: { label: 'Overdue', color: '#dc2626', bg: '#fee2e2' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' },
}

export const InvoiceHeader: FC<InvoiceHeaderProps> = ({
  business,
  invoice,
  client,
}) => {
  const status = statusConfig[invoice.status] || statusConfig.draft

  return (
    <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-6">
      {/* Top row: Business info (left) + Soljar logo (right) */}
      <div className="flex items-start justify-between gap-4 mb-8">
        {/* Business info */}
        <div className="flex items-center gap-3">
          {business.logoUrl ? (
            <img
              src={business.logoUrl}
              alt={business.name}
              className="w-10 h-10 rounded object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded bg-[#111827] flex items-center justify-center">
              <span className="text-sm font-semibold text-white">
                {business.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h2 className="text-sm font-semibold text-[#111827]">{business.name}</h2>
            <p className="text-xs text-[#6b7280]">{business.email}</p>
          </div>
        </div>

        {/* Soljar logo */}
        <img
          src="/soljar-logo.svg"
          alt="soljar"
          className="w-8 h-8"
        />
      </div>

      {/* Invoice title row - invoice # + status badge */}
      <div className="flex items-center justify-between gap-4 mb-2">
        <h1 className="text-xl font-semibold text-[#111827]">
          {invoice.invoiceNumber}
        </h1>
        <span
          className="text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{ color: status.color, backgroundColor: status.bg }}
        >
          {status.label}
        </span>
      </div>

      {/* Dates */}
      <p className="text-sm text-[#6b7280] mb-6">
        Issued {formatDate(invoice.issuedDate)} · Due {formatDate(invoice.dueDate)}
      </p>

      {/* Bill to / From - two columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Bill to (Client) */}
        <div>
          <p className="text-xs font-medium text-[#6b7280] uppercase tracking-wide mb-2">
            Bill to
          </p>
          <p className="text-sm font-medium text-[#111827]">{client.name}</p>
          <p className="text-sm text-[#6b7280]">{client.email}</p>
          {client.address && (
            <p className="text-sm text-[#6b7280] mt-1 whitespace-pre-line">{client.address}</p>
          )}
          {client.phone && (
            <p className="text-sm text-[#6b7280] mt-1">{client.phone}</p>
          )}
          {client.taxId && (
            <p className="text-sm text-[#6b7280] mt-1">Tax ID: {client.taxId}</p>
          )}
        </div>

        {/* From (Business) - right aligned */}
        <div className="sm:text-right">
          <p className="text-xs font-medium text-[#6b7280] uppercase tracking-wide mb-2">
            From
          </p>
          <p className="text-sm font-medium text-[#111827]">{business.name}</p>
          <p className="text-sm text-[#6b7280]">{business.email}</p>
          {business.address && (
            <p className="text-sm text-[#6b7280] mt-1 whitespace-pre-line">{business.address}</p>
          )}
          {business.phone && (
            <p className="text-sm text-[#6b7280] mt-1">{business.phone}</p>
          )}
        </div>
      </div>
    </div>
  )
}
